/**
 * Coaching (privacy policy, "Coaching (optional)"): the words the founder's switch and the admin's read-only state
 * use. Pure; `now` is injectable for tests. The API (../api/src/domain/coaching.ts) decides who may flip it.
 */
import { when } from './format'
import type { Coaching, CoachingMessage, CoachingReceived, CoachingSession, CoachingView } from './schemas'

export const PRIVACY_URL = 'https://cpg.computer/privacy'

/**
 * The admin's one line, for the founder page and a Fleet row: "Coaching on since Oct 12, 2:05 PM", or "Coaching off".
 * Missing (an API from before the switch) reads as off, the default.
 */
export function coachingLine(c: Coaching | undefined, now = Date.now()): string {
  if (!c?.on) return 'Coaching off'
  return c.since ? `Coaching on since ${when(c.since, now)}` : 'Coaching on'
}

/** What the policy says coaching lets us do, in the founder's settings. */
export const COACHING_WHAT =
  "Turn coaching on, and our software sends us your coding agents' conversations from your computer: your prompts, their replies, and the code in them. Someone on our team reads them to see how you work and suggest what to try next. Our team can also pair a device to your computer, watch your agents work and message them for you."

export const COACHING_OFF_WORDS =
  'Turn it off at any time. We stop receiving conversations at once, and our team stops using any device paired to your computer.'

/** The confirm step before turning it on, naming the company it covers. */
export function turnOnQuestion(company: string): string {
  return `Turn coaching on for ${company}? Our team may read your coding agents' conversations, including the code in them, and pair a device to your computer. You can turn it off here at any time.`
}

/** Why the caller can't turn it on, beside the greyed button; null when they can. */
export function turnOnReason(v: CoachingView): string | null {
  return v.can.turnOn ? null : 'Only a founder can turn coaching on.'
}

/** One line of the record: "Turned on by maya@fieldday.example". */
export function historyLine(h: CoachingView['history'][number]): string {
  return `${h.on ? 'Turned on' : 'Turned off'} by ${h.by}`
}

// ---------- What cpg.computer holds, and deleting it (the founder's Account) ----------

/**
 * After the policy's off words: what arrived stays until the founder deletes it. An API from before coaching reading
 * sends no `received`; then the policy's own words ("Ask us to delete what we received").
 */
export function keptWords(v: Pick<CoachingView, 'can' | 'received'>): string {
  if (v.received === undefined) return 'Ask us to delete what we received, and we will.'
  return v.can.deleteData ? 'What cpg.computer already received stays until you delete it here.' : 'What cpg.computer already received stays until your founder deletes it.'
}

/** Near the turn-on control: the first turn-on sends the last 60 days; a later one sends only from that moment. */
export const FIRST_ON_WORDS =
  'The first time coaching is turned on, your computer also sends the last 60 days of conversations. Turning it on again later sends only from that moment.'

const dayFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`

/** "cpg.computer holds 3 conversations, 42 messages, the latest from Oct 12. Kept 90 days." */
export function heldLine(r: CoachingReceived): string {
  if (r.sessions === 0) return `cpg.computer holds no conversations from your coding agents. Anything it receives is kept ${r.keepDays} days.`
  const latest = r.lastAt ? `, the latest from ${dayFmt.format(new Date(r.lastAt))}` : ''
  return `cpg.computer holds ${plural(r.sessions, 'conversation')}, ${plural(r.messages, 'message')}${latest}. Kept ${r.keepDays} days.`
}

/** The confirm before the founder deletes what we received. */
export function deleteQuestion(company: string, on: boolean): string {
  const after = on ? ' Coaching stays on, so new conversations keep arriving until you turn it off.' : ''
  return `Delete every conversation cpg.computer received from ${company}? This can't be undone.${after}`
}

/** What the delete did. */
export function deletedLine(d: { sessions: number; messages: number }): string {
  return d.sessions === 0 && d.messages === 0 ? 'There was nothing to delete.' : `Deleted ${plural(d.sessions, 'conversation')} and ${plural(d.messages, 'message')}.`
}

// ---------- The admin's reader (src/sections/coaching) ----------

const AGENT_NAMES: Record<string, string> = { claude: 'Claude Code', codex: 'Codex', grok: 'Grok' }
/** "Claude Code"; an agent this app doesn't know yet keeps its own name. */
export const agentName = (agent: string) => AGENT_NAMES[agent] ?? agent

/** "42 messages · 18.2k characters". `bytes` counts characters (the API's word). */
export function sizeWords(s: Pick<CoachingSession, 'messages' | 'bytes'>): string {
  const c = s.bytes
  const chars = c < 1000 ? `${c}` : c < 1_000_000 ? `${(c / 1000).toFixed(c < 10_000 ? 1 : 0)}k` : `${(c / 1_000_000).toFixed(1)}M`
  return `${plural(s.messages, 'message')} · ${chars} characters`
}

/** Who wrote the prompts: the people on that Linux user, or the user alone. */
export function whoWords(s: Pick<CoachingSession, 'user' | 'people'>): string {
  return s.people.length ? s.people.map((p) => p.email).join(', ') : s.user
}

export const sessionTitle = (s: Pick<CoachingSession, 'title'>) => s.title?.trim() || 'Untitled conversation'

/** The sessions whose title, project, user, people or agent hold the search. */
export function filterSessions(list: CoachingSession[], q: string): CoachingSession[] {
  const needle = q.trim().toLowerCase()
  if (!needle) return list
  return list.filter((s) => [s.title ?? '', s.project ?? '', s.user, agentName(s.agent), ...s.people.map((p) => p.email)].some((v) => v.toLowerCase().includes(needle)))
}

/** A message's text in pieces: prose, and ``` (or ~~~) fenced code with its language. An unclosed fence runs to the end. */
export type MessagePart = { kind: 'text'; text: string } | { kind: 'code'; lang: string | null; code: string }

export function splitFences(text: string): MessagePart[] {
  const out: MessagePart[] = []
  const prose: string[] = []
  let code: { fence: string; lang: string | null; lines: string[] } | null = null
  const flushProse = () => {
    const t = prose.join('\n').trim()
    if (t) out.push({ kind: 'text', text: t })
    prose.length = 0
  }
  for (const line of text.split('\n')) {
    if (code) {
      const close = /^\s*(`{3,}|~{3,})\s*$/.exec(line)
      if (close && close[1]![0] === code.fence[0] && close[1]!.length >= code.fence.length) {
        out.push({ kind: 'code', lang: code.lang, code: code.lines.join('\n') })
        code = null
      } else code.lines.push(line)
      continue
    }
    const open = /^\s*(`{3,}|~{3,})\s*([^\s`]*)\s*$/.exec(line)
    if (open) {
      flushProse()
      code = { fence: open[1]!, lang: open[2] || null, lines: [] }
    } else prose.push(line)
  }
  if (code) out.push({ kind: 'code', lang: code.lang, code: code.lines.join('\n') })
  flushProse()
  return out
}

/** Prose as paragraphs (split on blank lines), each a list of its lines, so line breaks survive without CSS. */
export const paragraphs = (text: string): string[][] =>
  text
    .split(/\n\s*\n/)
    .map((p) => p.split('\n').map((l) => l.trimEnd()))
    .filter((p) => p.some((l) => l.trim()))

/** Text cut where the search matches, case-insensitively, so the page can mark the hits. */
export function highlight(text: string, q: string): { text: string; hit: boolean }[] {
  const needle = q.trim().toLowerCase()
  if (!needle) return [{ text, hit: false }]
  const out: { text: string; hit: boolean }[] = []
  const lower = text.toLowerCase()
  let at = 0
  for (let i = lower.indexOf(needle); i >= 0; i = lower.indexOf(needle, i + needle.length)) {
    if (i > at) out.push({ text: text.slice(at, i), hit: false })
    out.push({ text: text.slice(i, i + needle.length), hit: true })
    at = i + needle.length
  }
  if (at < text.length) out.push({ text: text.slice(at), hit: false })
  return out
}

export const messageMatches = (m: Pick<CoachingMessage, 'text' | 'toolName'>, q: string) => {
  const needle = q.trim().toLowerCase()
  return !needle || m.text.toLowerCase().includes(needle) || (m.toolName ?? '').toLowerCase().includes(needle)
}

/**
 * Which tool entries are calls and which are results. The API sends both as role `tool` with the tool's name; a
 * Claude result's id ends in `.out`. Otherwise a tool's first entry is its call and the next with the same name its
 * result (parallel calls pair up in order). A prompt closes anything still open.
 */
export function toolKinds(messages: CoachingMessage[]): Map<string, 'call' | 'result'> {
  const kinds = new Map<string, 'call' | 'result'>()
  const open = new Map<string, number>()
  for (const m of messages) {
    if (m.role === 'user') open.clear()
    if (m.role !== 'tool') continue
    const name = m.toolName ?? ''
    const waiting = open.get(name) ?? 0
    const result = m.id.endsWith('.out') || waiting > 0
    kinds.set(m.id, result ? 'result' : 'call')
    open.set(name, result ? Math.max(0, waiting - 1) : waiting + 1)
  }
  return kinds
}

/** One line for a folded tool entry: a call's command, file or query when its input is JSON, else its first line. */
export function toolSummary(text: string, max = 110): string {
  let line = text.trim().split('\n').find((l) => l.trim()) ?? ''
  if (text.trim().startsWith('{')) {
    try {
      const j = JSON.parse(text) as Record<string, unknown>
      const pick = ['command', 'cmd', 'file_path', 'path', 'pattern', 'query', 'url', 'description'].map((k) => j[k]).find((v) => typeof v === 'string' || Array.isArray(v))
      if (pick !== undefined) line = Array.isArray(pick) ? pick.join(' ') : String(pick)
    } catch {
      // not JSON after all: the first line it is
    }
  }
  line = line.trim()
  return line.length > max ? `${line.slice(0, max - 1)}…` : line
}

/** The off state's words, naming the founder. */
export const offTitle = (name: string) => `Coaching is off for ${name}.`
